import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import clsx from 'clsx';
import type * as React from 'react';
import { forwardRef } from 'react';

import { Icon } from '@gitbook/spine-icons';

import { AtomButton, type AtomButtonProps } from '../Button';

type DragKnobProps = AtomButtonProps & {
    isDragging: boolean;
    listeners?: DraggableSyntheticListeners;
    children?: React.ReactNode;
    isDragHandleFocused?: boolean;
};

/**
 * A drag handle knob, to be used with draggable elements.
 * TODO: should be merged with the DragHandle within Tree.tsx
 */
export const DragKnob = forwardRef<HTMLButtonElement, DragKnobProps>((props, ref) => {
    const { isDragging = false, listeners, children, ...atomButtonProps } = props;

    return (
        <AtomButton
            data-exclude-from-copy
            ref={ref}
            {...listeners}
            {...atomButtonProps}
            className={clsx(
                'group/knob cursor-grab',
                isDragging && 'cursor-grabbing',
                atomButtonProps.className
            )}
        >
            {/* Optional children, e.g. to add additional UI like drag lines */}
            {children}
            <div
                className={clsx(
                    '-translate-x-1/2 absolute top-1.5 left-1/2 z-20',
                    'flex h-[0.375rem] w-6 shrink-0 items-center justify-center rounded-[0.375rem] bg-[var(--sp-color-button-drag-bg-default)] py-1.5',
                    // Defaults to opacity 0. Becomes visible on hover, focus, and while dragging
                    'opacity-0 transition-opacity',
                    // Hover and focus states
                    'group-hover/knob:opacity-100 group-hover/knob:shadow-focus-ring',
                    'group-focus/knob:opacity-100 group-focus/knob:shadow-focus-ring',
                    isDragging && 'scale-105 opacity-100'
                )}
            >
                <Icon.MoreHorizontal size={16} className="text-inverted-standout" />
            </div>
        </AtomButton>
    );
});
